import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { AccessDenied, errorStatus } from '@/components/RouteError';
import { usePageHeader } from '@/components/PageHeader';
import { useConfirm } from '@/components/DialogProvider';
import { useToast } from '@/components/ToastProvider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { RuleForm } from '@/components/finance/RuleForm';
import { apartmentsQuery } from '@/lib/apartments';
import { errorMessage } from '@/lib/errors';
import {
  financeAccountsQuery,
  financeCategoriesQuery,
  financeCounterpartiesQuery,
  financeRulesQuery,
  financeSettingsQuery,
  useRebuildFinanceRule,
  useUpdateFinanceRule,
} from '@/lib/finance';
import { usePermissions } from '@/lib/me';
import { formatMinor } from '@/lib/money';
import type { FinanceRule } from '@/api/generated/types.gen';

export const Route = createFileRoute('/_app/finance/rules')({ component: RulesPage });

function RulesPage() {
  return <RulesScreen />;
}

const WEEKDAY_SHORT = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Exported for finance.rules.$id.tsx's read-only fallback (same table, same
// wording) — a route file mixing a component export with plain helpers
// disables react-refresh's single-export-per-file check, same as
// DialogProvider.tsx's useConfirm/usePrompt.
// eslint-disable-next-line react-refresh/only-export-components
export function typeLabel(t: FinanceRule['type']): string {
  return t === 'recurring' ? 'Recurring' : t === 'per_stay' ? 'Per stay' : 'Commission invoice';
}

/** One-line human summary of a rule's type-specific params, for the table's
 * Schedule column — reads the opaque FinanceRuleParams defensively (a shape
 * the server would already have rejected via ValidateRuleParams if wrong,
 * but this reads whatever the wire actually sent, not what it should be). */
// eslint-disable-next-line react-refresh/only-export-components
export function scheduleSummary(rule: FinanceRule, ruleNames: Record<string, string>): string {
  if (rule.type === 'recurring') {
    const s = (rule.params as { schedule?: { kind?: string; days?: number[]; weekday?: number; anchor?: string } })
      .schedule;
    if (!s?.kind) return '—';
    if (s.kind === 'monthly') return `Monthly (day ${s.days?.[0] ?? '?'})`;
    if (s.kind === 'twice_monthly') return `Twice monthly (${(s.days ?? []).join(', ')})`;
    if (s.kind === 'weekly') return `Weekly (${WEEKDAY_SHORT[s.weekday ?? 0] ?? '?'})`;
    if (s.kind === 'biweekly') return `Every 2 weeks (${WEEKDAY_SHORT[s.weekday ?? 0] ?? '?'}, from ${s.anchor ?? '?'})`;
    return s.kind;
  }
  if (rule.type === 'per_stay') {
    const trigger = (rule.params as { trigger?: string }).trigger;
    const base = trigger === 'checkout' ? 'On check-out' : 'On check-in';
    if (rule.parent_rule_id) {
      const pct = rule.percent_bp != null ? `${rule.percent_bp / 100}%` : '?%';
      return `${base} · ${pct} of ${ruleNames[rule.parent_rule_id] ?? rule.parent_rule_id}`;
    }
    return base;
  }
  const dueDay = (rule.params as { due_day?: number }).due_day;
  return `Booking.com invoice, due day ${dueDay ?? '?'}`;
}

export function RulesScreen() {
  const { can } = usePermissions();
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const [includeDisabled, setIncludeDisabled] = useState(true);
  const [creating, setCreating] = useState(false);

  const rules = useQuery(financeRulesQuery({ includeDisabled }));
  const { data: categories = [] } = useQuery(financeCategoriesQuery());
  const { data: apartments = [] } = useQuery(apartmentsQuery);
  const { data: counterparties = [] } = useQuery(financeCounterpartiesQuery());
  const { data: accounts = [] } = useQuery(financeAccountsQuery());
  const { data: settings } = useQuery(financeSettingsQuery);
  const update = useUpdateFinanceRule();
  const rebuild = useRebuildFinanceRule();

  const items = rules.data?.items ?? [];
  const canManage = can('finance.manage');

  usePageHeader({
    subtitle: rules.data ? `${items.length} rules` : undefined,
    actions: canManage ? (
      <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
        <Plus className="h-4 w-4" />
        New rule
      </Button>
    ) : undefined,
  });

  // Same belt-and-suspenders as finance.queue.tsx: the client check alone
  // would leave a finance.submit-only actor able to reach the real table by
  // typing the URL, since no nav link gets them here in the first place.
  if (!can('finance.read') || errorStatus(rules.error) === 403) return <AccessDenied />;

  const apartmentNames = Object.fromEntries(apartments.map((a) => [a.id, a.title]));
  const ruleNames = Object.fromEntries(items.map((r) => [r.id, r.name]));

  function onToggle(rule: FinanceRule) {
    update.mutate(
      { id: rule.id, body: { enabled: !rule.enabled } },
      { onError: (e) => toast({ kind: 'error', title: 'Could not update rule', message: errorMessage(e) }) },
    );
  }

  async function onRebuild(rule: FinanceRule) {
    const ok = await askConfirm({
      title: `Rebuild "${rule.name}"?`,
      body: 'Re-walks every booking this rule could apply to and reconciles its expected payments — creates, updates, cancels or revives rows to match the rule as it stands now. Company-scoped or non-per_stay rules have nothing to walk.',
      confirmLabel: 'Rebuild',
    });
    if (!ok) return;
    rebuild.mutate(rule.id, {
      onSuccess: (r) =>
        toast({
          kind: 'success',
          title: 'Rebuild complete',
          // All five counters, every time — a rule this is a no-op for (spec:
          // company-scoped or non-per_stay) answers all zeros, and an
          // operator who cannot tell that apart from "nothing needed doing"
          // has no way to notice a rebuild that silently did not apply.
          message: `${r.bookings} bookings · ${r.created} created · ${r.updated} updated · ${r.cancelled} cancelled · ${r.revived} revived`,
          // Five numbers in one line need more than the 5s default
          // (ToastProvider's DEFAULT_MS) to actually read before it dismisses.
          durationMs: 15_000,
        }),
      onError: (e) => toast({ kind: 'error', title: 'Could not rebuild', message: errorMessage(e) }),
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <Checkbox
        label="Show disabled rules"
        checked={includeDisabled}
        onChange={(e) => setIncludeDisabled(e.target.checked)}
      />

      {rules.isLoading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : rules.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(rules.error)}
        </p>
      ) : items.length === 0 ? (
        <p className="rounded-[var(--radius-card)] border border-dashed border-[var(--border-hairline)] bg-card p-8 text-center text-sm text-muted">
          No rules yet.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-hairline)] text-left text-xs uppercase tracking-[0.06em] text-muted">
                <th className="px-4 py-2.5 font-semibold">Type</th>
                <th className="px-4 py-2.5 font-semibold">Name</th>
                <th className="px-4 py-2.5 font-semibold">Object</th>
                <th className="px-4 py-2.5 font-semibold">Amount</th>
                <th className="px-4 py-2.5 font-semibold">Schedule</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold">Generated through</th>
                {canManage && <th className="px-4 py-2.5 font-semibold">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const hideAmount = r.type === 'per_stay' && !!r.parent_rule_id;
                return (
                  <tr key={r.id} className="border-t border-[var(--border-hairline)] hover:bg-[var(--surface-sunken)]">
                    <td className="px-4 py-3">{typeLabel(r.type)}</td>
                    <td className="px-4 py-3">
                      <Link to="/finance/rules/$id" params={{ id: r.id }} className="font-medium text-primary hover:underline">
                        {r.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      {r.scope === 'company' ? 'Company' : (apartmentNames[r.apartment_id ?? ''] ?? r.apartment_id)}
                    </td>
                    <td className="px-4 py-3 tabular-nums">{hideAmount ? '—' : formatMinor(r.amount_minor, r.currency)}</td>
                    <td className="px-4 py-3">{scheduleSummary(r, ruleNames)}</td>
                    <td className="px-4 py-3">
                      {r.enabled ? (
                        <Badge variant="success" dot>
                          Enabled
                        </Badge>
                      ) : (
                        <Badge variant="neutral" dot>
                          Disabled
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">{r.generated_through ?? '—'}</td>
                    {canManage && (
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          {/* isPending alone is true for EVERY row while any one
                              of them is in flight (one shared mutation instance) —
                              pairing it with .variables (the row the LAST
                              mutate() call actually targeted) keeps the spinner
                              on the row the operator clicked, not the whole table. */}
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => onToggle(r)}
                            loading={update.isPending && update.variables?.id === r.id}
                          >
                            {r.enabled ? 'Disable' : 'Enable'}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => void onRebuild(r)}
                            loading={rebuild.isPending && rebuild.variables === r.id}
                          >
                            Rebuild
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <Dialog open onClose={() => setCreating(false)} side title="New rule">
          <RuleForm
            refs={{
              categories,
              apartments: apartments.map((a) => ({ id: a.id, title: a.title })),
              counterparties,
              accounts,
              parentCandidates: items,
              importCutoff: settings?.import_cutoff,
            }}
            onDone={() => {
              setCreating(false);
              toast({ kind: 'success', title: 'Rule created' });
            }}
            onCancel={() => setCreating(false)}
          />
        </Dialog>
      )}
    </div>
  );
}
